<script setup>
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import SupportTicketAttachmentsField from './SupportTicketAttachmentsField.vue';
import { useSupportTicketAttachments } from '../composables/useSupportTicketAttachments';

defineProps({
  ticketTypeOptions: { type: Array, default: () => [] },
  priorityOptions: { type: Array, default: () => [] },
  categoryOptions: { type: Array, default: () => [] },
  isCreating: { type: Boolean, default: false },
  canSubmitTicket: { type: Boolean, default: false },
});

const emit = defineEmits(['submit']);

const ticketForm = defineModel({ type: Object, required: true });

const { t } = useI18n();
const { attachmentLimitLabel, appendFiles } = useSupportTicketAttachments();

const onTicketFilesSelected = event => {
  appendFiles(ticketForm.value, event.target.files);
  event.target.value = '';
};

const removeTicketAttachment = index => {
  ticketForm.value.attachments.splice(index, 1);
};
</script>

<template>
  <LaisCard
    :title="t('SUPPORT_TICKETS.FORM.OPEN_TITLE')"
    prefix-icon="i-lucide-circle-plus"
  >
    <form class="grid gap-4" @submit.prevent="emit('submit')">
      <div class="grid gap-3 sm:grid-cols-2">
        <LaisField :label="t('SUPPORT_TICKETS.FORM.TYPE')">
          <select
            v-model="ticketForm.ticket_type"
            class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
          >
            <option
              v-for="item in ticketTypeOptions"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </option>
          </select>
        </LaisField>

        <LaisField :label="t('SUPPORT_TICKETS.FORM.PRIORITY')">
          <select
            v-model="ticketForm.priority"
            class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
          >
            <option
              v-for="item in priorityOptions"
              :key="item.value"
              :value="item.value"
            >
              {{ item.label }}
            </option>
          </select>
        </LaisField>
      </div>

      <LaisField :label="t('SUPPORT_TICKETS.FORM.CATEGORY')">
        <input
          v-model.trim="ticketForm.category"
          type="text"
          list="support-ticket-categories"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
          :placeholder="t('SUPPORT_TICKETS.FORM.CATEGORY_PLACEHOLDER')"
        />
        <datalist id="support-ticket-categories">
          <option
            v-for="item in categoryOptions"
            :key="item.value"
            :value="item.value"
          />
        </datalist>
      </LaisField>

      <LaisField :label="t('SUPPORT_TICKETS.FORM.SUBJECT')" required>
        <input
          v-model.trim="ticketForm.subject"
          type="text"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
          :placeholder="t('SUPPORT_TICKETS.FORM.SUBJECT_PLACEHOLDER')"
        />
      </LaisField>

      <LaisField :label="t('SUPPORT_TICKETS.FORM.DESCRIPTION')" required>
        <textarea
          v-model.trim="ticketForm.description"
          rows="4"
          class="min-h-[112px] resize-y rounded-lais border border-lais-border bg-white px-3 py-2 text-[14px] text-lais-text outline-none focus:border-lais-primary"
          :placeholder="t('SUPPORT_TICKETS.FORM.DESCRIPTION_PLACEHOLDER')"
        />
      </LaisField>

      <LaisField :label="t('SUPPORT_TICKETS.FORM.VISIBILITY')">
        <select
          v-model="ticketForm.visibility"
          class="h-10 rounded-lais border border-lais-border bg-white px-3 text-[14px] text-lais-text outline-none focus:border-lais-primary"
        >
          <option value="private">
            {{ t('SUPPORT_TICKETS.FORM.PRIVATE') }}
          </option>
          <option value="account">
            {{ t('SUPPORT_TICKETS.FORM.ACCOUNT') }}
          </option>
        </select>
      </LaisField>

      <SupportTicketAttachmentsField
        :attachments="ticketForm.attachments"
        :limit-label="attachmentLimitLabel"
        @change="onTicketFilesSelected"
        @remove="index => removeTicketAttachment(index)"
      />

      <div class="flex justify-end">
        <LaisPrimaryButton
          type="submit"
          icon="i-lucide-send"
          :is-loading="isCreating"
          :disabled="!canSubmitTicket"
        >
          {{
            isCreating && ticketForm.attachments.length
              ? t('SUPPORT_TICKETS.ATTACHMENTS.UPLOADING')
              : t('SUPPORT_TICKETS.FORM.SUBMIT')
          }}
        </LaisPrimaryButton>
      </div>
    </form>
  </LaisCard>
</template>
